/**
 * CRM Note Feed
 *
 * Shared note feed for a CRM entity (company or contact). Renders a rich-text
 * composer on top, then the entity's notes newest-first. Each note shows the
 * sanitized HTML body, its author, a timestamp, and a ⋮ menu (Edit + Delete).
 *
 * Edit happens inline via a small dialog that reuses the rich-text editor;
 * Delete is confirmed through an AlertDialog. Styling mirrors the activity
 * timeline card surface.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StickyNote, MoreVertical } from 'lucide-react';
import DOMPurify from 'dompurify';
import { formatDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { NoteComposer } from '../activity';
import {
  useEntityNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from '../../hooks/useCrmNotes';
import type { CrmNote, CrmNoteEntityType } from '../../api/crmNotesApi';

interface CrmNoteFeedProps {
  entityType: CrmNoteEntityType;
  entityId: string;
}

/** Strip HTML to plain text for emptiness checks. */
function htmlToText(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ');
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

export function CrmNoteFeed({ entityType, entityId }: CrmNoteFeedProps) {
  const { t } = useTranslation('crm');
  const { data: notes, isLoading } = useEntityNotes(entityType, entityId);
  const createMut = useCreateNote(entityType, entityId);
  const updateMut = useUpdateNote(entityType, entityId);
  const deleteMut = useDeleteNote(entityType, entityId);

  const [editing, setEditing] = useState<CrmNote | null>(null);
  const [editBody, setEditBody] = useState('');
  const [deleting, setDeleting] = useState<CrmNote | null>(null);

  const openEdit = (note: CrmNote): void => {
    setEditing(note);
    setEditBody(note.bodyHtml);
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editing || !htmlToText(editBody).trim()) return;
    await updateMut.mutateAsync({ noteId: editing.id, bodyHtml: editBody });
    setEditing(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    await deleteMut.mutateAsync(deleting.id);
    setDeleting(null);
  };

  return (
    <div className="space-y-4">
      <NoteComposer
        entityId={entityId}
        isSaving={createMut.isPending}
        onSubmit={async (html) => {
          await createMut.mutateAsync(html);
        }}
      />

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
              <Skeleton className="h-4 w-1/3 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : !notes || notes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <StickyNote className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p>{t('notes.noNotes')}</p>
          <p className="text-xs mt-1">{t('notes.noNotesHint')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-sm text-gray-500">{formatDateTime(note.createdAt)}</div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(note)}>{t('notes.menuEdit')}</DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDeleting(note)}
                      className="text-red-600"
                    >
                      {t('notes.menuDelete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div
                className="prose prose-sm max-w-none text-gray-700 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.bodyHtml) }}
              />

              <p className="text-xs text-gray-400 mt-3">
                {t('notes.createdBy', { name: note.createdByUserName })}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('notes.editTitle')}</DialogTitle>
          </DialogHeader>
          <RichTextEditor
            value={editBody}
            onChange={setEditBody}
            placeholder={t('notes.editPlaceholder')}
            disabled={updateMut.isPending}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={updateMut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateMut.isPending || !htmlToText(editBody).trim()}
            >
              {updateMut.isPending ? t('notes.saving') : t('notes.saveNote')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notes.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notes.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMut.isPending ? t('notes.deleting') : t('notes.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
